/**
 * Editor de texto flotante: un textarea alineado con la figura para que escribir
 * se vea igual que el resultado final (notas, cuadros de texto y figuras con texto).
 */
import type { Shape } from '../../shared/types.ts'
import { fitText, fontString, measureTextHeight } from '../canvas/text.ts'
import { displayInk } from '../canvas/render-shape.ts'
import { setTopicText, topicFontSize, topicPadding } from '../mindmap/actions.ts'
import type { App } from '../app.ts'

export class TextEditor {
  readonly el: HTMLTextAreaElement
  private app: App
  private shapeId: string | null = null
  private measure: CanvasRenderingContext2D

  constructor(app: App) {
    this.app = app
    this.measure = document.createElement('canvas').getContext('2d')!
    this.el = document.createElement('textarea')
    this.el.className = 'text-editor hidden'
    this.el.spellcheck = false
    this.el.autocapitalize = 'sentences'
    this.el.addEventListener('input', () => this.onInput())
    this.el.addEventListener('blur', () => this.close())
    this.el.addEventListener('keydown', (e) => this.onKeyDown(e))
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation())
    app.store.subscribe(() => this.reposition())
  }

  get isOpen(): boolean {
    return this.shapeId !== null
  }

  open(id: string, selectAll = false): void {
    const shape = this.app.board.get(id)
    if (
      !shape ||
      (shape.type !== 'note' && shape.type !== 'text' && shape.type !== 'shape' && shape.type !== 'topic')
    )
      return
    this.shapeId = id
    this.app.store.setEditing(id)
    this.app.renderer.textHidden.add(id)
    this.app.renderer.invalidate()
    this.el.classList.remove('hidden')
    this.el.value = shape.type === 'shape' || shape.type === 'topic' ? (shape.text ?? '') : shape.text
    this.reposition()
    // Foco inmediato para no perder las primeras teclas de quien escribe rápido…
    this.el.focus({ preventScroll: true })
    this.setCaret(selectAll)
    // …y otra vez tras el layout, que es cuando iOS decide abrir el teclado.
    requestAnimationFrame(() => {
      if (this.shapeId !== id) return
      if (document.activeElement !== this.el) {
        this.el.focus({ preventScroll: true })
        this.setCaret(selectAll)
      }
    })
  }

  private setCaret(selectAll: boolean): void {
    if (selectAll) this.el.select()
    else this.el.setSelectionRange(this.el.value.length, this.el.value.length)
  }

  close(): void {
    if (!this.shapeId) return
    const id = this.shapeId
    this.shapeId = null
    const shape = this.app.board.get(id)
    const text = this.el.value
    this.el.classList.add('hidden')
    this.el.value = ''
    this.app.renderer.textHidden.delete(id)
    this.app.store.setEditing(null)

    if (shape) {
      // Un cuadro de texto vacío no deja rastro (como en Whiteboard).
      if (shape.type === 'text' && text.trim() === '') {
        this.app.board.delete([id])
        this.app.store.setSelection([])
      } else {
        this.applyText(shape, text)
      }
    }
    this.app.renderer.invalidate()
    this.app.touch()
  }

  private onKeyDown(e: KeyboardEvent): void {
    e.stopPropagation()
    if (e.key === 'Escape') {
      e.preventDefault()
      this.el.blur()
    }
    // Enter confirma en figuras y temas; Shift+Enter hace salto de línea.
    if (e.key === 'Enter' && !e.shiftKey) {
      const shape = this.shapeId ? this.app.board.get(this.shapeId) : null
      if (shape && (shape.type === 'shape' || shape.type === 'topic')) {
        e.preventDefault()
        this.el.blur()
      }
    }
    // Tab dentro de un tema: confirma y crea un hijo (flujo XMind).
    if (e.key === 'Tab') {
      const shape = this.shapeId ? this.app.board.get(this.shapeId) : null
      if (shape?.type === 'topic') {
        e.preventDefault()
        this.el.blur()
        this.app.mindmapAddChild(shape.id)
      }
    }
  }

  private onInput(): void {
    if (!this.shapeId) return
    const shape = this.app.board.get(this.shapeId)
    if (!shape) return
    this.applyText(shape, this.el.value)
    this.reposition()
  }

  private applyText(shape: Shape, text: string): void {
    if (shape.type === 'topic') {
      // El tamaño del tema depende del texto: la acción corre el layout.
      setTopicText(this.app.board, shape.id, text)
    } else if (shape.type === 'note') {
      this.app.board.update(shape.id, { text })
    } else if (shape.type === 'shape') {
      this.app.board.update(shape.id, { text })
    } else if (shape.type === 'text') {
      const height = measureTextHeight(this.measure, text, shape.w, shape.fontSize, shape.bold, shape.italic)
      this.app.board.update(shape.id, { text, h: height })
    }
  }

  reposition(): void {
    if (!this.shapeId) return
    const shape = this.app.board.get(this.shapeId)
    if (!shape) {
      this.close()
      return
    }
    const { camera } = this.app.store
    const z = camera.z
    const topLeft = this.app.renderer.worldToScreen(shape.x, shape.y)
    const style = this.el.style

    if (shape.type === 'topic') {
      const { px, py } = topicPadding(shape.depth)
      const fontSize = topicFontSize(shape.depth)
      style.left = `${topLeft.x + px * z}px`
      style.top = `${topLeft.y + py * z}px`
      style.width = `${Math.max(40, shape.w - px * 2) * z}px`
      style.height = `${Math.max(20, shape.h - py * 2) * z}px`
      style.fontSize = `${fontSize * z}px`
      style.lineHeight = `${fontSize * 1.3 * z}px`
      style.textAlign = 'center'
      style.fontWeight = shape.depth === 0 ? '600' : '400'
      style.color = shape.depth === 0 ? '#FFFFFF' : displayInk('#242424')
      // Heredados de la última nota o texto editado: con el relleno superior de
      // una nota (≈68 px) el texto del tema quedaba fuera de la caja, invisible.
      style.paddingTop = '0'
      style.fontStyle = 'normal'
      return
    }

    if (shape.type === 'note') {
      const pad = 14
      const inner = { w: shape.w - pad * 2, h: shape.h - pad * 2 }
      const layout = fitText(
        this.measure,
        this.el.value || ' ',
        inner.w,
        inner.h,
        Math.min(shape.fontSize, shape.h / 3.2),
      )
      style.left = `${topLeft.x + pad * z}px`
      style.top = `${topLeft.y + pad * z}px`
      style.width = `${inner.w * z}px`
      style.height = `${inner.h * z}px`
      style.font = fontString(layout.fontSize * z)
      style.lineHeight = `${layout.lineHeight * z}px`
      style.color = '#1A1A1A'
      style.textAlign = shape.align
      style.paddingTop = `${Math.max(0, (inner.h * z - layout.height * z) / 2)}px`
    } else if (shape.type === 'shape') {
      const pad = 12
      const inner = { w: Math.max(10, shape.w - pad * 2), h: Math.max(10, shape.h - pad * 2) }
      const layout = fitText(this.measure, this.el.value || ' ', inner.w, inner.h, shape.fontSize ?? 20)
      style.left = `${topLeft.x + pad * z}px`
      style.top = `${topLeft.y + pad * z}px`
      style.width = `${inner.w * z}px`
      style.height = `${inner.h * z}px`
      style.font = fontString(layout.fontSize * z)
      style.lineHeight = `${layout.lineHeight * z}px`
      style.color = displayInk(shape.textColor ?? shape.color)
      style.textAlign = 'center'
      style.paddingTop = `${Math.max(0, (inner.h * z - layout.height * z) / 2)}px`
    } else if (shape.type === 'text') {
      style.left = `${topLeft.x}px`
      style.top = `${topLeft.y}px`
      style.width = `${shape.w * z}px`
      style.height = `${Math.max(shape.h, shape.fontSize * 1.25) * z}px`
      style.font = fontString(shape.fontSize * z, shape.bold, shape.italic)
      style.lineHeight = `${shape.fontSize * 1.25 * z}px`
      style.color = displayInk(shape.color)
      style.textAlign = shape.align
      style.paddingTop = '0'
    }
  }
}
